import React, { useMemo, useState } from "react";
import { Navigate, Link } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Badge } from "../../app/components/ui/badge";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { useAuth } from "../../providers/AuthProvider";

function statusBadgeVariant(status: string) {
  if (status === "verified") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

export function PendingVerificationPage() {
  const { user, business, refreshMe, logout, isLoading } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = business?.status ?? "pending";

  const statusText = useMemo(() => {
    if (status === "verified") return "Verified";
    if (status === "rejected") return "Rejected";
    return "Pending verification";
  }, [status]);

  async function onRefresh() {
    setError(null);
    setIsRefreshing(true);
    try {
      await refreshMe();
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to refresh status");
    } finally {
      setIsRefreshing(false);
    }
  }

  if (!isLoading && business?.status === "verified") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Business verification</CardTitle>
          <CardDescription>
            Your business needs verification before you can access POS features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Refresh failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="rounded-lg border border-border bg-card p-4 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm text-muted-foreground">Business</div>
                <div className="font-medium truncate">{business?.name ?? "—"}</div>
              </div>
              <Badge variant={statusBadgeVariant(status) as any}>{statusText}</Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Signed in as <span className="text-foreground font-medium">{user?.email ?? "—"}</span>
            </div>
          </div>

          {status === "rejected" ? (
            <div className="rounded-lg border bg-destructive/5 p-4 space-y-2">
              <div className="text-sm font-medium text-destructive">Rejection reason</div>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                {business?.rejectionReason ?? "—"}
              </div>
            </div>
          ) : null}

          <div className="flex gap-2">
            <Button onClick={onRefresh} disabled={isRefreshing}>
              {isRefreshing ? "Refreshing..." : "Refresh status"}
            </Button>
            <Button variant="outline" onClick={logout}>
              Logout
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/login">Back to login</Link>
            </Button>
          </div>

          <div className="text-xs text-muted-foreground">
            If you’re a super admin, verify businesses at{" "}
            <Link className="text-primary hover:underline" to="/admin/verifications">
              /admin/verifications
            </Link>
            .
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

